import { Controller } from "react-hook-form";
import type {
  Control,
  UseFormRegister,
  UseFormWatch,
  UseFormTrigger,
  FieldErrors,
} from "react-hook-form";
import type { Geofence } from "../../../../../../../store/admin/geofencesStore";
import type { ThemeClasses } from "../../types";
import { CoordinateInput } from "./CoordinateInput";

interface CircularCoordinatesFormProps {
  control: Control<Geofence>;
  register: UseFormRegister<Geofence>;
  watch: UseFormWatch<Geofence>;
  trigger: UseFormTrigger<Geofence>;
  errors: FieldErrors<Geofence>;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  defaultLatitude: number;
  defaultLongitude: number;
}

export function CircularCoordinatesForm({
  control,
  register,
  watch,
  trigger,
  errors,
  themeClasses,
  isDarkMode,
  defaultLatitude,
  defaultLongitude,
}: CircularCoordinatesFormProps) {
  const inputClassName = `w-full text-xs 2xl:text-base px-3 py-2 pr-16 ${
    isDarkMode ? "bg-dark-card" : "bg-gray-50"
  } border ${themeClasses.cardBorder} rounded-lg ${
    themeClasses.text
  } focus:outline-none focus:ring-2 focus:ring-primary-500`;

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-2">
          <label
            className={`block text-sm font-medium ${themeClasses.text} mb-1`}
          >
            Latitud
          </label>
          <div className="flex items-center gap-2">
            <Controller
              name="coordinates.latitude"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <CoordinateInput
                  value={Number(field.value ?? defaultLatitude)}
                  onChange={field.onChange}
                  className={inputClassName}
                  placeholder="Ej: 19.4326"
                  ariaLabel="Latitud"
                />
              )}
            />
          </div>
          {errors.coordinates &&
            !Array.isArray(errors.coordinates) &&
            (errors.coordinates as any).latitude && (
              <span className="text-red-500 text-xs">Campo obligatorio</span>
            )}
        </div>
        <div className="flex flex-col gap-2">
          <label
            className={`block text-sm font-medium ${themeClasses.text} mb-1`}
          >
            Longitud
          </label>
          <div className="flex items-center gap-2">
            <Controller
              name="coordinates.longitude"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <CoordinateInput
                  value={Number(field.value ?? defaultLongitude)}
                  onChange={field.onChange}
                  className={inputClassName}
                  placeholder="Ej: -99.1332"
                  ariaLabel="Longitud"
                />
              )}
            />
          </div>
          {errors.coordinates &&
            !Array.isArray(errors.coordinates) &&
            (errors.coordinates as any).longitude && (
              <span className="text-red-500 text-xs">Campo obligatorio</span>
            )}
        </div>
      </div>

      <div>
        <div className="w-full flex items-center gap-3">
          <label
            className={`block text-sm font-medium ${themeClasses.text} mb-1`}
          >
            Radio (metros):
          </label>
          <span
            className={`w-[10%] flex text-sm font-medium ${themeClasses.text}`}
          >
            {watch("coordinates.radius")} m
          </span>
        </div>
        <input
          type="range"
          min={50}
          max={2000}
          {...register("coordinates.radius", {
            required: true,
            min: 50,
            max: 2000,
            onChange: () => {
              trigger("coordinates.radius");
            },
          })}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-500"
        />
        <div className="w-full flex justify-between text-xs text-gray-400 mt-1">
          <span>50 m</span>
          <span>2000 m</span>
        </div>
        {errors.coordinates &&
          !Array.isArray(errors.coordinates) &&
          (errors.coordinates as any).radius && (
            <span className="text-red-500 text-xs">Radio entre 50 y 2000</span>
          )}
      </div>
    </>
  );
}
